import { ChangeDetectorRef, Component, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { CoreMenuItem } from '@core/types';
import { CoreMenuService } from '@core/components/core-menu/core-menu.service';

import { User } from 'app/auth/models';
import { AuthenticationService } from 'app/auth/service';
import { SocketIoService } from 'app/main/services/io.service';
import { WEBSOCKET_EVENT } from 'app/main/shared/enums/WebsocketEvent.enum';
import { ToastrService } from 'ngx-toastr';
import { NOTIFICATION_TYPE } from 'app/main/shared/enums/Notification.enum';
import { IMessage } from 'app/main/shared/interfaces/whatsapp.interface';
import { WhatsappNotificationService } from 'app/main/services/whatsapp-notification.service';
import { HttpErrorResponse } from '@angular/common/http';
import { NotificationService } from 'app/main/services/notifications.service';

@Component({
  selector: '[core-menu-vertical-collapsible]',
  templateUrl: './collapsible.component.html'
})
export class CoreMenuVerticalCollapsibleComponent implements OnInit, OnDestroy {
  currentUser: User;

  @Input()
  item: CoreMenuItem;

  @HostBinding('class.open')
  public isOpen = false;

  // Private
  private _unsubscribeAll: Subject<any>;
  private notificationCount: number;
  public hasArchiveNotification: boolean;
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  /**
   * Constructor
   *
   * @param {Router} _router
   * @param {CoreMenuService} _coreMenuService
   * @param {ChangeDetectorRef} _changeDetectorRef
   */
  constructor(
    private _router: Router,
    private _coreMenuService: CoreMenuService,
    private _auth: AuthenticationService,
    private readonly _socket: SocketIoService,
    private readonly _toastr: ToastrService,
    private readonly _notification: WhatsappNotificationService,
    private _changeDetectorRef: ChangeDetectorRef
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Listen for router events and expand
    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe((event: NavigationEnd) => {
        // Confirm if the urlAfterRedirects can be found in one of the children of this item
        if (this.confirmUrlInChildren(this.item, event.urlAfterRedirects)) {
          this.expand();
        } else {
          this.collapse();
        }
      });

    // Subscribe to the current menu changes
    this._coreMenuService.onMenuChanged.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
      this.currentUser = this._coreMenuService.currentUser;
    });

    // Listen for collapsing of any menu item
    this._coreMenuService.onItemCollapsed.pipe(takeUntil(this._unsubscribeAll)).subscribe(clickedItem => {
      if (clickedItem && clickedItem.children) {
        // Check if the clicked item is one of the children of this item
        if (this.confirmItemInChildren(this.item, clickedItem)) {
          return;
        }

        // Check if the url can be found in one of the children of this item
        if (this.confirmUrlInChildren(this.item, this._router.url)) {
          return;
        }

        // If the clicked item is not this item, collapse...
        if (this.item !== clickedItem) {
          this.collapse();
        }
      }
    });

    // Check if the url can be found in one of the children of this item
    // Required for onInit case (i.e switching theme customizer menu layout)
    if (this.confirmUrlInChildren(this.item, this._router.url)) {
      this.expand();
    } else {
      this.collapse();
    }

    
    if (this.item.data?.hasNotifications && this.isSender) {
      this.getUnreadMessages(this._auth.companyId);
      if (!this.notificationCount)
        this.notificationCount = 0;
      this._socket
        .listen(WEBSOCKET_EVENT.WHATSAPP_NOTIFICATION)
        .subscribe((objeto: any) => {
          this.getUnreadMessages(this._auth.companyId);
          // this.incrementNotificationCount();
        });
      // this._socket
      //   .listen(WEBSOCKET_EVENT.WHATSAPP_CHAT)
      //   .subscribe((objeto: { company_id: number, message: IMessage}) => {
      //     this._toastr.info(objeto.message.payload.body);
      //     console.log("WHATSAPP NOTIFICATION ", objeto.company_id);
      //     this.incrementNotificationCount();
      //   });
    }
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  // Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle collapse
   *
   * @param e
   */
  toggleOpen(e): void {
    e.preventDefault();

    this.isOpen = !this.isOpen;

    // Menu collapse toggled...
    this._coreMenuService.onItemCollapsed.next(this.item);
    this._coreMenuService.onItemCollapseToggled.next();

    this.deleteNotificationBalloon();
  }

  /**
   * Expand the collapsible menu
   */
  expand(): void {
    if (this.isOpen) {
      return;
    }

    this.isOpen = true;

    // Mark for check
    this._changeDetectorRef.markForCheck();

    this._coreMenuService.onItemCollapseToggled.next();
  }

  /**
   * Collapse the collapsible menu
   */
  collapse(): void {
    if (!this.isOpen) {
      return;
    }

    this.isOpen = false;

    // Mark for check
    this._changeDetectorRef.markForCheck();

    this._coreMenuService.onItemCollapseToggled.next();
  }

  /**
   * Confirms if the provided url can be found in one of the given parent's children
   *
   * @param parent
   * @param url
   * @returns {boolean}
   */
  confirmUrlInChildren(parent, url): boolean {
    const children = parent.children;

    // Return false if parent don't have any children
    if (!children) {
      return false;
    }

    // Loop all the children
    for (const child of children) {
      // If children has child (Sub to sub item url)
      if (child) {

        if (child.children) {
          // Call function again with child
          if (this.confirmUrlInChildren(child, url)) {
            return true;
          }
        }

        // If child.url is same as provided url
        if (child.url === url || url.includes(child.url)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Check if the provided parent has the provided item in one of its children
   *
   * @param parent
   * @param item
   * @returns {boolean}
   */
  confirmItemInChildren(parent, item): boolean {
    const children = parent.children;

    // Return false if parent don't have any children
    if (!children) {
      return false;
    }

    // Return true parent has the provided item in one of its children
    if (children.indexOf(item) > -1) {
      return true;
    }

    for (const child of children) {
      if (child) {
        if (child.children) {
          // Call function again with child (for sub to sub item)
          if (this.confirmItemInChildren(child, item)) {
            return true;
          }
        }
      }
    }

    return false;
  }

  getUnreadMessages(companyId: number) {
  this._notification.getWaUnreadMessages(companyId)
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe((response) => {
      if (this.notificationCount < response.count) {
        this._toastr.info("Nuevo mensaje de WhatsApp!");
      }
      this.notificationCount = response.count;
      this._changeDetectorRef.detectChanges();
    },
    (err: HttpErrorResponse) => {
      console.error("Error al obtener mensajes no leidos", err);
      this.notificationCount = 0;
    }); 
  }

/*   getLoadedFile(companyId: number) {
    this._notificationService.getLoadedFile(companyId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((response) => {
        if (this.notificationCount < response) {
          this._toastr.info("Reporte listo para descargar!");
        }
        this.notificationCount = 1;
        this._changeDetectorRef.detectChanges();
      },
      (err: HttpErrorResponse) => {
        console.error("Error al obtener reporte", err);
        this.notificationCount = 0;
      }); 
    } */


  incrementNotificationCount = () => {
    this.notificationCount++;
    this._changeDetectorRef.detectChanges();
    //localStorage.setItem('notificationCount', `${this.notificationCount}`);
  }

  deleteNotificationBalloon = () => {
    if (this.notificationCount) {
      this.notificationCount = 0;
      this._changeDetectorRef.detectChanges();
      //localStorage.setItem('notificationCount', `${this.notificationCount}`);
    }
  }

  get isSender() {
    return this._auth.isSender;
  }
}